import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext();

const PUBLIC_PREFIXES = ['/', '/explore', '/map', '/event', '/artist', '/login', '/register'];

// Bump this when we want to force-reset every user's stored theme preference.
// Previously some users had landed with light mode persisted in localStorage —
// this version bump migrates them back to dark by default.
const THEME_VERSION = '2';
const THEME_VERSION_KEY = 'plancity_theme_v';
const THEME_KEY = 'plancity_theme';

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    try {
      const storedVersion = localStorage.getItem(THEME_VERSION_KEY);
      if (storedVersion !== THEME_VERSION) {
        // First load on this version — wipe any old preference, default to dark.
        localStorage.removeItem(THEME_KEY);
        localStorage.setItem(THEME_VERSION_KEY, THEME_VERSION);
        return true;
      }
      const saved = localStorage.getItem(THEME_KEY);
      // Default to dark unless the user explicitly chose light.
      return saved === null ? true : saved === 'dark';
    } catch {
      return true;
    }
  });

  const location = useLocation();

  const isPublicPage = PUBLIC_PREFIXES.some(prefix => {
    if (prefix === '/') return location.pathname === '/';
    return location.pathname.startsWith(prefix);
  });

  const effectiveDark = isDark;

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, isDark ? 'dark' : 'light');
    } catch { /* ignore */ }
  }, [isDark]);

  const toggleTheme = () => setIsDark(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDark: effectiveDark, rawDark: isDark, toggleTheme, isPublicPage }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
